import { Tabs, Tab } from "@mui/material";
import useRestTriggerConfig, { EMPTY_ENDPOINT_OPTIONS, PostLoginRestTriggerConfig } from "./restTriggers";
import { useState, useEffect } from "react";
import { useHeaderEditor, isValidHeader } from "./headerEdit";
import { optionsFromList, useOptionSelect } from "./optionSelect";
import { useTextEntry } from "./textEntry";
import { isAlphaNumeric, isAlphaNumericPath, isAlphaNumericWithUnderScores, isValidURL } from "../utils/validation";
import { 
    FormWithMargin, 
    LightContainerWithMargin,
    DarkContainerWithMargin,
    ErrorContainerWithMargin
} from "../styles/styledComponents";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { base64Decode, base64Encode } from "../utils/base64";
import { ProxySettingsData, BLANK_HEADER, HeaderData } from "../utils/types";
import usePolicyList from "./policyNames";
import { Box } from "@mui/system";

export function useProxySettingsEditor(
    displayError: boolean,
    initialName="",
    initialMethod="",
    initialPolicy="",
    initialEndpoint="",
    initialHeaders: HeaderData[] = []
) { 
    // this bit will handle the header editor
    // we can have multiple headers, so we need to cycle through them
    const [headers, setHeaders] = useState<HeaderData[]>(initialHeaders);
    const [headerIndex, setHeaderIndex] = useState<number>(0);
    const showHeaderEditor = headers.length > 0;
    const headerInitialValues = showHeaderEditor ? headers[headerIndex % headers.length] : BLANK_HEADER;
    const {
        component: headerEditor,
        isValid: headerIsValid,
        headerData: headerDisplayValue,
        setHeader: setHeaderDisplayValue
    } = useHeaderEditor(
        displayError,
        headerInitialValues.destinationHeader,
        headerInitialValues.value,
        headerInitialValues.source
    );

    const cycleHeader = (index: number) => {
        // Update the header in the array
        const newHeaders = [...headers];
        newHeaders[headerIndex] = headerDisplayValue;
        setHeaders(newHeaders);
        const newIndex = (index + headers.length) % headers.length;
        setHeaderIndex(newIndex);
    };
    
    const addHeader = () => {
        const newHeaders = [...headers];
        // make sure current header is saved
        if(headerIsValid) {
            newHeaders[headerIndex] = headerDisplayValue;
        }
        newHeaders.push(BLANK_HEADER);
        setHeaders(newHeaders);
        setHeaderIndex(newHeaders.length - 1);
    };

    const removeHeader = () => {
        const newHeaders = [...headers];
        newHeaders.splice(headerIndex, 1);
        const newIndex = (headerIndex + newHeaders.length) % newHeaders.length;
        setHeaders(newHeaders);
        setHeaderIndex(newIndex);
    };

    useEffect(() => {
        if(headers.length) setHeaderDisplayValue(headers[headerIndex]);
    }, [headers, headerIndex])

    //TODO style this bad boi
    const cycleHeaderButtons = <>
        <Button onClick={() => cycleHeader(headerIndex - 1)}>Previous</Button>
        <Typography>Header {headerIndex + 1} / {headers.length}</Typography>
        <Button onClick={() => cycleHeader(headerIndex + 1)}>Next</Button>
    </>

    const removeHeaderButton = <Button onClick={() => removeHeader()}>
        Remove Header
    </Button>

    //Proxy name
    const {
        component: nameComponent,
        value: nameValue,
        setValue: setName,
        isValid: nameIsValid
    } = useTextEntry(initialName, displayError, 'Name', isAlphaNumeric);
    
    const {
        component: pathComponent,
        value: pathValue,
        setValue: setPath,
        isValid: pathIsValid
    } = useTextEntry(initialName, displayError, 'Path', isAlphaNumericPath);
    
    // http method 
    const {
        component: methodComponent,
        selectedOption: methodValue,
        setSelection: setMethod,
        isOptionSelected: methodIsValid
    } = useOptionSelect(
        "HTTP Method", 
        optionsFromList(["GET", "POST", "PUT", "PATCH"]),
        displayError
    );
    const [showEndpointError, setShowEndpointError] = useState(false);
    const {
        component: endpointConfigComponent,
        restConfig,
        setRestConfig,
        isValid: isEndpointValid
    } = useRestTriggerConfig(showEndpointError);
    const [endpointList, setEndpointList] = useState<PostLoginRestTriggerConfig[]>([]);
    const addEndpointConfig = () => {
        console.log(isEndpointValid);
        const newlist = [...endpointList];
        if (!isEndpointValid) {
            setShowEndpointError(true)
            return;
        }
        setShowEndpointError(false);
        newlist.push(restConfig);
        setEndpointList(newlist);
        setRestConfig(EMPTY_ENDPOINT_OPTIONS);
    };
    const removeEndpoint = (ind: number) => {
        const newList = [...endpointList];
        newList.splice(ind, 1);
        setEndpointList(newList);
    };
    const addEndpointConfigButton = <Button onClick={() => addEndpointConfig()}>Add Endpoint</Button>
    const endpointListComp = <>
        <Typography variant='h6'>Configured Endpoints</Typography>
        {endpointList.map( (endpoint, ind) => <LightContainerWithMargin key={ind}>
            <Button onClick={() => {removeEndpoint(ind)}} style={{color:"white", fontSize: "1.2rem", fontWeight:"600"}}>Delete</Button>
            <Typography>{endpoint.endpoint}</Typography>
            <Typography>Auth: {endpoint.authType}</Typography>
            {endpoint.claims.map((claim, index) => <Typography key={index}>{claim}</Typography>)}
        </LightContainerWithMargin>)}
    </>
    // b2cPolicyString
    //TODO make this a selection dropdown from data pulled from the b2c tenant
    const {
        component: policyComponent,
        policyList: policyValue,
        setPolicyList: setPolicy,
    } = usePolicyList(
        [],
        isAlphaNumericWithUnderScores
    );
    const policyIsValid = true;
    // endpoint URL
    const {
        component: endpointComponent,
        value: endpointValue,
        setValue: setEndpoint,
        isValid: endpointIsValid
    } = useTextEntry(initialEndpoint, displayError, 'Endpoint', isValidURL);

    const isValid = nameIsValid 
    && policyIsValid 
    && endpointIsValid 
    && pathIsValid
    && headers.every(isValidHeader);
    
    const setProxySettings = (proxySettings: ProxySettingsData) => {
        console.log(`Setting proxy settings`);
        console.log(proxySettings);
        if(!proxySettings.displayName) {
            setName(proxySettings.name)
        } else {
            setName(proxySettings.displayName);
        }
        setPath(proxySettings.name);
        setPolicy(proxySettings.b2cPolicy);
        setMethod(proxySettings.method);
        setEndpoint(
            base64Decode(proxySettings.endpoint)
        );
        setHeaders(proxySettings.headers);
        setEndpointList(proxySettings.restTriggers);
    };

    const proxySettingsData: ProxySettingsData = {
        name: pathValue,
        displayName: nameValue,
        b2cPolicy: policyValue,
        method: methodValue,
        endpoint: base64Encode(endpointValue),
        headers: headers,
        restTriggers: endpointList
    };

    const headerInfoDisplay = headers.map((header, index) => {
        if(index === headerIndex) {
            header = headerDisplayValue;
        }
        if(!isValidHeader(header)) {
            return <ErrorContainerWithMargin key={index}>
                <Typography variant='h6'>Header {index + 1}</Typography>
                <Typography>Destination Header: {header.destinationHeader}</Typography>
                <Typography>Source: {header.source}</Typography>
                <Typography>Value: {header.value}</Typography>
            </ErrorContainerWithMargin>
        }
        if(index === headerIndex) {
            header = headerDisplayValue;
            return <LightContainerWithMargin key={index}>
            <Typography variant='h6'>Header {index + 1}</Typography>
            <Typography>Destination Header: {header.destinationHeader}</Typography>
            <Typography>Source: {header.source}</Typography>
            <Typography>Value: {header.value}</Typography>
        </LightContainerWithMargin>
        }
        return <DarkContainerWithMargin key={index}>
            <Typography variant='h6'>Header {index + 1}</Typography>
            <Typography>Destination Header: {header.destinationHeader}</Typography>
            <Typography>Source: {header.source}</Typography>
            <Typography>Value: {header.value}</Typography>
        </DarkContainerWithMargin>
    });
    const [tabIndex, setTabIndex] = useState(0);
    const component = <>
        <FormWithMargin>
            <Typography>Edit Endpoint Configuration</Typography>
            <Tabs value={tabIndex} onChange={(e, ind) => setTabIndex(ind)}>
                <Tab label="Basic Config"/>
                <Tab label="Header Config" />
                <Tab label="Post Login Config" />
            </Tabs>
            <Box hidden={tabIndex!==0}>
                <div style={{display: "flex", flexDirection: "column"}}>
                    {nameComponent}
                    {pathComponent}
                    {endpointComponent}
                </div> 
                {policyComponent}
            </Box>
            <Box hidden={tabIndex!==1}>
                {showHeaderEditor && headerEditor}
                <span style={{display: "flex", flexDirection:"row", alignItems: "center", justifyContent:"center"}}>
                    <Button onClick={addHeader}>Add Header</Button>
                    {showHeaderEditor && removeHeaderButton}
                </span>
            </Box>
            <Box hidden={tabIndex!==1}>
                    <Typography>Current Headers</Typography>
                    {headers.length > 0? headerInfoDisplay: 
                        <LightContainerWithMargin>
                            <Typography>No headers</Typography>
                        </LightContainerWithMargin>
                    }
                <span style={{display: "flex", flexDirection:"row", alignItems: "center", justifyContent:"center"}}>
                        {headers.length > 1 && cycleHeaderButtons}
                    </span>
            </Box>
            <Box hidden={tabIndex!==2}>
                <Typography>Post Login REST Config</Typography>
                {addEndpointConfigButton}
                {endpointConfigComponent}
                {endpointListComp}
            </Box>
        </FormWithMargin>
    </>

    return {
        component,
        isValid,
        proxySettingsData,
        setProxySettings
    }
}
